import {Feature} from "geojson";
import * as turf from '@turf/turf';
import { darkenColor } from './Colors';


export const nullCellColor = () => {
    return `rgb(0, 0, 0)`;
};

export const getColor = (value, min, max) => {
    const nullCell = nullCellColor();
    if (value === null || min === undefined || max === undefined) {
        return nullCell;
    }

    if (max - min === 0) {
        return `rgb(255, 0, 0)`;
    }

    let ratio = (value - min) / (max - min);
    if (!isFinite(ratio) || isNaN(ratio)) {
        ratio = 0;
    }

    if (ratio < 0.5) {
        const r = 255;
        const g = Math.round(510 * ratio);
        const b = 0;
        return `rgb(${r}, ${g}, ${b})`;
    } else {
        const r = Math.round(255 - 510 * (ratio - 0.5));
        const g = 255;
        const b = 0;
        return `rgb(${Math.max(0, r)}, ${g}, ${b})`;
    }
};

/**
 *
 * @param mapObjects {Feature[]}
 * @return {{}}
 */
export function createEmptyMapObjectValues(mapObjects) {
    const relations = {};
    mapObjects.forEach(element => {
        relations[element.properties?.object_id] = 0;
    });
    return relations;
}

const HEX_FILL_ALPHA = 0.9;
const HEX_NULL_COLOR = `rgba(50,50,50,0.6)`;

export function getSortedNumericValues(valuesMap = {}) {
    return Object.values(valuesMap)
        .filter((v) => typeof v === "number" && !isNaN(v))
        .sort((a, b) => a - b);
}

export function interpolateHexFillColor(t, alpha = HEX_FILL_ALPHA) {
    const colorRed = [255, 0, 0];
    const colorYellow = [255, 255, 0];
    const colorGreen = [0, 200, 0];
    const clamped = Math.max(0, Math.min(1, Number(t) || 0));
    const mix = (start, end, p) => {
        const r = Math.round(start[0] + (end[0] - start[0]) * p);
        const g = Math.round(start[1] + (end[1] - start[1]) * p);
        const b = Math.round(start[2] + (end[2] - start[2]) * p);
        return `rgba(${r},${g},${b},${alpha})`;
    };
    if (clamped <= 0.5) return mix(colorRed, colorYellow, clamped / 0.5);
    return mix(colorYellow, colorGreen, (clamped - 0.5) / 0.5);
}

function getQuantileThresholds(sortedValues, quantileCount) {
    const thresholds = [];
    const len = sortedValues.length;
    for (let i = 1; i < quantileCount; i++) {
        const q = i / quantileCount;
        const pos = (len - 1) * q;
        const base = Math.floor(pos);
        const rest = pos - base;
        const value = sortedValues[base] + rest * ((sortedValues[base + 1] ?? sortedValues[base]) - sortedValues[base]);
        thresholds.push(value);
    }
    return thresholds;
}

function getQuantileIndex(val, thresholds) {
    for (let i = 0; i < thresholds.length; i++) {
        if (val <= thresholds[i]) return i;
    }
    return thresholds.length;
}

export function colorForMappedValue(value, sortedValues, isCompetitorsLayer = false) {
    if (typeof value !== "number" || isNaN(value) || !sortedValues?.length) {
        return HEX_NULL_COLOR;
    }
    if (sortedValues.length === 1) {
        return interpolateHexFillColor(isCompetitorsLayer ? 0 : 1);
    }
    const quantileCount = Math.min(sortedValues.length, 10);
    const thresholds = getQuantileThresholds(sortedValues, quantileCount);
    const qIndex = getQuantileIndex(value, thresholds);
    const t = qIndex / Math.max(quantileCount - 1, 1);
    return interpolateHexFillColor(isCompetitorsLayer ? (1 - t) : t);
}

export function legendQuantileStops(sortedValues, isCompetitorsLayer = false) {
    const quantileCount = Math.min(Math.max(sortedValues.length, 1), 10);
    if (!sortedValues.length) return [];
    const stops = [];
    for (let i = 0; i < quantileCount; i++) {
        const t = quantileCount === 1 ? 1 : i / (quantileCount - 1);
        const color = interpolateHexFillColor(isCompetitorsLayer ? (1 - t) : t);
        const pct = quantileCount === 1 ? 0 : (i / (quantileCount - 1)) * 100;
        stops.push({ color, pct });
    }
    return stops;
}

/**
 * @param valuesMap {{number: number}}
 * @param features {Feature[]}
 * @param isCompetitorsLayer {boolean}
 * @return {Feature[]}
 */
export function createHexes(valuesMap, features = null, isCompetitorsLayer = false, gradientMode = false) {
    const sortedValues = getSortedNumericValues(valuesMap);

    return features.map(feature => {
        const featureValue = valuesMap[feature.properties.object_id]
            ?? valuesMap[String(feature.properties.object_id)];
        const color = colorForMappedValue(featureValue, sortedValues, isCompetitorsLayer);

        const outlineColor = gradientMode
            ? `rgba(0,0,0,0)`
            : darkenColor(color, 0.7);

        return {
            ...feature,
            properties: {
                ...feature.properties,
                value: featureValue,
                color,
                originalColor: color,
                outlineColor
            }
        };
    });
}

export function buildNeighborMap(features) {
    const map = {};
    features.forEach(f => { map[f.properties.object_id] = []; });

    for (let i = 0; i < features.length; i++) {
        const a = features[i];
        const idA = a.properties.object_id;
        const polyA = turf.polygon(a.geometry.coordinates);
        for (let j = i + 1; j < features.length; j++) {
            const b = features[j];
            const idB = b.properties.object_id;
            try {
                if (turf.booleanIntersects(polyA, turf.polygon(b.geometry.coordinates))) {
                    map[idA].push(idB);
                    map[idB].push(idA);
                }
            } catch (e) {}
        }
    }
    return map;
}

/**
 * Сглаживает значения карты усреднением по соседним гексагонам
 * @param {Object} valuesMap - {object_id: value}
 * @param {Feature[]} features - массив гексагонов
 * @param {number} iterations - количество проходов сглаживания (1-3)
 * @returns {Object} сглаженный valuesMap
 */
export function smoothValuesMap(valuesMap, features, iterations = 1, neighborMap = null) {
    if (!features?.length || !valuesMap) return valuesMap;

    const neighborsMap = neighborMap ?? buildNeighborMap(features);

    let smoothed = { ...valuesMap };
    for (let iter = 0; iter < iterations; iter++) {
        const newValues = {};
        for (const id in smoothed) {
            const current = smoothed[id];
            if (current == null) { newValues[id] = null; continue; }
            const neighborIds = neighborsMap[id] || [];
            let sum = current, count = 1;
            neighborIds.forEach(nid => {
                const val = smoothed[nid];
                if (val != null) { sum += val; count++; }
            });
            newValues[id] = sum / count;
        }
        smoothed = newValues;
    }
    return smoothed;
}